import React from "react";
import { Info, User } from "lucide-react";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Switch } from "../../ui/switch";
import { FormData, FormErrors, sanitizeName, sanitizeEmail, sanitizePhone, sanitizeRFC, sanitizeCURP } from "../../../hooks/useCreateRecordForm";

interface Step1InquilinoProps {
  formData: FormData;
  errors: FormErrors;
  updateFormData: <K extends keyof FormData>(key: K, value: FormData[K]) => void;
  validateField: (fieldName: keyof FormData) => boolean;
  isAnimating: boolean;
}

export const Step1Inquilino: React.FC<Step1InquilinoProps> = ({
  formData,
  errors,
  updateFormData,
  validateField,
  isAnimating,
}) => {
  return (
    <div
      className={`space-y-6 transition-all duration-500 ${isAnimating ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"}`}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2 pb-2">
          <User className="w-4 h-4 text-gray-700" />
          <h3 className="text-base font-semibold text-gray-900">
            Datos del Inquilino
          </h3>
        </div>
        <p className="text-sm text-gray-500 -mt-4">
          Información básica del inquilino para el expediente
        </p>
      </div>

      {/* Contract renewal (MB-362) — visibility for CX */}
      <div className="space-y-4">
        <div className="flex items-center justify-between py-4 px-6 bg-gray-50 rounded-lg border border-gray-200 transition-all duration-200 ease-in-out hover:shadow-sm">
          <div className="pr-4">
            <Label htmlFor="isRenewal" className="text-sm font-medium text-gray-700">
              Renovación de contrato
            </Label>
            <p id="isRenewal-help" className="text-xs text-gray-500 mt-0.5">
              Actívalo si este expediente es una renovación (se renueva con el mismo inquilino).
            </p>
          </div>
          <Switch
            id="isRenewal"
            aria-describedby="isRenewal-help"
            checked={formData.isRenewal}
            onCheckedChange={(checked) => {
              updateFormData("isRenewal", checked);
              if (!checked) updateFormData("previousFolio", "");
            }}
            className="data-[state=checked]:bg-gray-900"
          />
        </div>

        {formData.isRenewal && (
          <div className="animate-in fade-in slide-in-from-top-2 duration-300 ease-out">
            {/* MB-379: this flow is not the one-click renewal; say so, so CX don't expect copies. */}
            <div
              id="isRenewal-note"
              className="flex items-start gap-2 mb-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900"
            >
              <Info className="size-4 shrink-0 mt-px" aria-hidden="true" />
              <p>
                Aquí se crea un expediente nuevo con lo que captures: no se copian participantes,
                documentos ni datos del expediente anterior. Para renovar copiando todo, usa
                «Crear renovación» en el detalle del expediente anterior.
              </p>
            </div>
            <Label htmlFor="previousFolio" className="text-sm font-medium text-gray-700 mb-2 block">
              Folio del contrato anterior (opcional)
            </Label>
            <Input
              id="previousFolio"
              value={formData.previousFolio}
              onChange={(e) => updateFormData("previousFolio", e.target.value.replace(/\D/g, ""))}
              placeholder="Ej. 1024"
              inputMode="numeric"
              maxLength={10}
              aria-invalid={!!errors.previousFolio}
              aria-describedby={
                errors.previousFolio ? "isRenewal-note previousFolio-error" : "isRenewal-note"
              }
            />
            {errors.previousFolio && (
              <p id="previousFolio-error" className="text-xs text-red-600 mt-1">
                {errors.previousFolio}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Tipo de Persona */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700">
          Tipo de persona
        </Label>
        <RadioGroup
          value={formData.tenantType}
          onValueChange={(value) => updateFormData("tenantType", value as "fisica" | "moral")}
          className="grid grid-cols-1 sm:grid-cols-2 gap-3"
        >
          <label
            htmlFor="persona-fisica"
            className={`relative flex cursor-pointer rounded-md border p-3 transition-all hover:bg-gray-50 ${
              formData.tenantType === "fisica"
                ? "border-blue-500 bg-blue-50/50"
                : "border-gray-300"
            }`}
          >
            <RadioGroupItem
              value="fisica"
              id="persona-fisica"
              className="mt-0.5"
            />
            <div className="ml-2.5">
              <div className="text-sm font-medium text-gray-900">
                Persona Física
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                Individuos con RFC e identificación
              </div>
            </div>
          </label>
          <label
            htmlFor="persona-moral"
            className={`relative flex cursor-pointer rounded-md border p-3 transition-all hover:bg-gray-50 ${
              formData.tenantType === "moral"
                ? "border-blue-500 bg-blue-50/50"
                : "border-gray-300"
            }`}
          >
            <RadioGroupItem
              value="moral"
              id="persona-moral"
              className="mt-0.5"
            />
            <div className="ml-2.5">
              <div className="text-sm font-medium text-gray-900">
                Persona Moral
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                Empresas o entidades legales
              </div>
            </div>
          </label>
        </RadioGroup>
      </div>

      {/* Campos del formulario */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Nombre */}
          <div>
            <Label htmlFor="tenantName" className="text-sm font-medium text-gray-700 mb-2 block">
              Nombre *
            </Label>
            <Input
              id="tenantName"
              value={formData.tenantName}
              onChange={(e) => updateFormData("tenantName", sanitizeName(e.target.value))}
              onBlur={() => validateField("tenantName")}
              placeholder="Ingresa el nombre"
              aria-invalid={!!errors.tenantName}
              maxLength={50}
            />
            {errors.tenantName && (
              <p className="text-xs text-red-600 mt-1">{errors.tenantName}</p>
            )}
          </div>

          {/* Apellido paterno */}
          <div>
            <Label htmlFor="tenantFirstSurname" className="text-sm font-medium text-gray-700 mb-2 block">
              Apellido paterno *
            </Label>
            <Input
              id="tenantFirstSurname"
              value={formData.tenantFirstSurname}
              onChange={(e) => updateFormData("tenantFirstSurname", sanitizeName(e.target.value))}
              onBlur={() => validateField("tenantFirstSurname")}
              placeholder="Ingresa el apellido paterno"
              aria-invalid={!!errors.tenantFirstSurname}
              maxLength={50}
            />
            {errors.tenantFirstSurname && (
              <p className="text-xs text-red-600 mt-1">{errors.tenantFirstSurname}</p>
            )}
          </div>

          {/* Apellido materno */}
          <div>
            <Label htmlFor="tenantSecondSurname" className="text-sm font-medium text-gray-700 mb-2 block">
              Apellido materno
            </Label>
            <Input
              id="tenantSecondSurname"
              value={formData.tenantSecondSurname}
              onChange={(e) => updateFormData("tenantSecondSurname", sanitizeName(e.target.value))}
              onBlur={() => validateField("tenantSecondSurname")}
              placeholder="Ingresa el apellido materno"
              aria-invalid={!!errors.tenantSecondSurname}
              maxLength={50}
            />
            {errors.tenantSecondSurname && (
              <p className="text-xs text-red-600 mt-1">{errors.tenantSecondSurname}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Correo */}
          <div className="lg:col-span-2">
            <Label htmlFor="tenantEmail" className="text-sm font-medium text-gray-700 mb-2 block">
              Correo electrónico *
            </Label>
            <Input
              id="tenantEmail"
              type="email"
              value={formData.tenantEmail}
              onChange={(e) => updateFormData("tenantEmail", sanitizeEmail(e.target.value))}
              onBlur={() => validateField("tenantEmail")}
              placeholder="correo@ejemplo.com"
              className="lowercase"
              aria-invalid={!!errors.tenantEmail}
              maxLength={100}
            />
            {errors.tenantEmail && (
              <p className="text-xs text-red-600 mt-1">{errors.tenantEmail}</p>
            )}
          </div>

          {/* Teléfono */}
          <div>
            <Label htmlFor="tenantPhoneNumber" className="text-sm font-medium text-gray-700 mb-2 block">
              Teléfono *
            </Label>
            <Input
              id="tenantPhoneNumber"
              type="tel"
              value={formData.tenantPhoneNumber}
              onChange={(e) => updateFormData("tenantPhoneNumber", sanitizePhone(e.target.value))}
              onBlur={() => validateField("tenantPhoneNumber")}
              placeholder="1234567890"
              maxLength={10}
              aria-invalid={!!errors.tenantPhoneNumber}
            />
            {errors.tenantPhoneNumber && (
              <p className="text-xs text-red-600 mt-1">{errors.tenantPhoneNumber}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* RFC */}
          <div>
            <Label htmlFor="tenantRfc" className="text-sm font-medium text-gray-700 mb-2 block">
              RFC (opcional)
            </Label>
            <Input
              id="tenantRfc"
              value={formData.tenantRfc}
              onChange={(e) => updateFormData("tenantRfc", sanitizeRFC(e.target.value))}
              onBlur={() => validateField("tenantRfc")}
              placeholder="XAXX010101000"
              className="uppercase"
              aria-invalid={!!errors.tenantRfc}
              maxLength={13}
            />
            {errors.tenantRfc && (
              <p className="text-xs text-red-600 mt-1">{errors.tenantRfc}</p>
            )}
          </div>

          {/* CURP */}
          <div>
            <Label htmlFor="tenantCurp" className="text-sm font-medium text-gray-700 mb-2 block">
              CURP (opcional)
            </Label>
            <Input
              id="tenantCurp"
              value={formData.tenantCurp}
              onChange={(e) => updateFormData("tenantCurp", sanitizeCURP(e.target.value))}
              onBlur={() => validateField("tenantCurp")}
              placeholder="XAXX010101HDFXXX00"
              className="uppercase"
              aria-invalid={!!errors.tenantCurp}
              maxLength={18}
            />
            {errors.tenantCurp && (
              <p className="text-xs text-red-600 mt-1">{errors.tenantCurp}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
